// Configuración del sistema.
//
// El formulario NO está escrito a mano: se dibuja solo desde el registro que
// devuelve GET /api/admin/settings. Añadir una opción en `ajustes_registro.py`
// del backend basta para que aparezca aquí, validada y con su explicación.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, ErrorBanner, HelpBox, OkBanner, PageHeader, Spinner } from "../../components/Layout";
import {
  SettingsAPI,
  type CampoSchema,
  type PruebaAuditor,
  type PruebaIA,
  type SettingsPayload,
} from "../../api/endpoints";

type Valores = Record<string, unknown>;

function Campo({
  nombre,
  meta,
  valor,
  origen,
  marcadorSecreto,
  onChange,
}: {
  nombre: string;
  meta: CampoSchema;
  valor: unknown;
  origen?: "panel" | "env" | "fabrica";
  marcadorSecreto: string;
  onChange: (v: unknown) => void;
}) {
  const id = `campo-${nombre}`;

  const control = () => {
    switch (meta.tipo) {
      case "bool":
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              id={id}
              type="checkbox"
              className="accent-marca"
              checked={Boolean(valor)}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span className="text-sm text-tinta-2">
              {valor ? "Activado" : "Desactivado"}
            </span>
          </label>
        );
      case "enum":
        return (
          <select id={id} className="input" value={String(valor ?? "")} onChange={(e) => onChange(e.target.value)}>
            {(meta.opciones ?? []).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        );
      case "entero":
      case "decimal":
        return (
          <input
            id={id}
            type="number"
            className="input"
            step={meta.tipo === "decimal" ? "any" : 1}
            min={meta.min}
            max={meta.max}
            value={valor === null || valor === undefined ? "" : String(valor)}
            onChange={(e) =>
              onChange(e.target.value === "" ? null : Number(e.target.value))
            }
          />
        );
      case "json":
        return (
          <textarea
            id={id}
            className="input font-mono text-xs"
            rows={6}
            value={typeof valor === "string" ? valor : JSON.stringify(valor ?? {}, null, 2)}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case "secreto": {
        const guardado = valor === marcadorSecreto;
        return (
          <input
            id={id}
            type="password"
            className="input"
            autoComplete="new-password"
            placeholder={
              guardado
                ? "•••••••• guardado (escribe para reemplazarlo)"
                : "sin configurar"
            }
            value={guardado ? "" : String(valor ?? "")}
            onChange={(e) =>
              // Vacío = «no lo toqué»: se devuelve el marcador y el servidor
              // conserva lo guardado. Sin esto, tocar otro campo del grupo
              // borraría la credencial.
              onChange(e.target.value === "" ? marcadorSecreto : e.target.value)
            }
          />
        );
      }
      case "texto_largo":
        return (
          <textarea id={id} className="input" rows={4} value={String(valor ?? "")}
                    onChange={(e) => onChange(e.target.value)} />
        );
      default:
        return (
          <input id={id} type="text" className="input" value={String(valor ?? "")}
                 onChange={(e) => onChange(e.target.value)} />
        );
    }
  };

  return (
    <div className="mb-4">
      <label className="etiqueta" htmlFor={id}>
        {meta.titulo}
        {meta.env && (
          <span className="ml-2 font-normal text-tinta-3">
            · también en el .env como <code className="font-mono">{meta.env}</code>
            {origen && (
              <>
                {" "}— ahora manda:{" "}
                <b className={origen === "panel" ? "text-marca" : ""}>
                  {origen === "panel" ? "el panel" : origen === "env" ? "el .env" : "el valor de fábrica"}
                </b>
              </>
            )}
          </span>
        )}
      </label>
      {control()}
      {meta.descripcion && (
        <p className="mt-1 text-xs text-tinta-3 leading-relaxed">{meta.descripcion}</p>
      )}
    </div>
  );
}

function TarjetaGrupo({
  grupo,
  datos,
  borrador,
  setBorrador,
}: {
  grupo: string;
  datos: SettingsPayload;
  borrador: Record<string, Valores>;
  setBorrador: React.Dispatch<React.SetStateAction<Record<string, Valores>>>;
}) {
  const qc = useQueryClient();
  const [ok, setOk] = useState<string | null>(null);
  const esquema = datos.schema[grupo];

  const guardar = useMutation({
    mutationFn: () => {
      const aEnviar: Valores = {};
      for (const [campo, meta] of Object.entries(esquema.campos)) {
        const v = borrador[grupo]?.[campo];
        if (v === undefined) continue;
        if (meta.tipo === "json" && typeof v === "string") {
          aEnviar[campo] = JSON.parse(v || "{}"); // el error sube al onError
        } else {
          aEnviar[campo] = v;
        }
      }
      return SettingsAPI.save(grupo, aEnviar);
    },
    onSuccess: (res) => {
      setOk("Guardado ✅");
      // Se refresca SOLO este grupo: recargar todo pisaría lo que el operador
      // tenga escrito sin guardar en los demás.
      setBorrador((prev) => ({ ...prev, [grupo]: { ...res.valores } }));
      qc.invalidateQueries({ queryKey: ["uso-ia"] });
      setTimeout(() => setOk(null), 3000);
    },
  });

  return (
    <Card className="mb-5">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-tinta">{esquema.titulo}</h2>
        {esquema.descripcion && (
          <p className="text-sm text-tinta-3 mt-0.5">{esquema.descripcion}</p>
        )}
      </div>

      {Object.entries(esquema.campos).map(([campo, meta]) => (
        <Campo
          key={campo}
          nombre={`${grupo}-${campo}`}
          meta={meta}
          valor={borrador[grupo]?.[campo]}
          origen={datos.origenes[grupo]?.[campo]}
          marcadorSecreto={datos.marcador_secreto}
          onChange={(v) =>
            setBorrador((prev) => ({ ...prev, [grupo]: { ...prev[grupo], [campo]: v } }))
          }
        />
      ))}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button className="btn" disabled={guardar.isPending} onClick={() => guardar.mutate()}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </button>
        {guardar.isPending && <Spinner />}
        {ok && <span className="text-sm text-estado-ok">{ok}</span>}
      </div>
      {guardar.isError && (
        <div className="mt-3">
          <ErrorBanner message={(guardar.error as Error).message} />
        </div>
      )}
    </Card>
  );
}

function TarjetaIA() {
  const [prueba, setPrueba] = useState<PruebaIA | null>(null);
  const uso = useQuery({ queryKey: ["uso-ia"], queryFn: SettingsAPI.usoIA });

  const probar = useMutation({
    mutationFn: SettingsAPI.probarIA,
    onSuccess: (r) => setPrueba(r),
    onError: () => setPrueba(null),
  });

  return (
    <Card className="mb-5">
      <h2 className="text-lg font-semibold text-tinta mb-1">Estado de la IA</h2>
      <p className="text-sm text-tinta-3 mb-3">
        «Probar» hace una llamada real con el prompt vigente: comprueba la clave, el modelo
        y la conectividad de una vez.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn" disabled={probar.isPending} onClick={() => probar.mutate()}>
          {probar.isPending ? "Probando…" : "Probar IA"}
        </button>
        {probar.isPending && <Spinner />}
      </div>

      {probar.isError && (
        <div className="mt-3"><ErrorBanner message={(probar.error as Error).message} /></div>
      )}

      {prueba && (
        <div className="mt-3">
          <OkBanner
            message={
              `Respondió ${prueba.modelo} (${prueba.proveedor}) en ${prueba.latencia_ms} ms · ` +
              `${prueba.tokens_entrada} tokens de entrada (${prueba.tokens_desde_cache} desde caché), ` +
              `${prueba.tokens_salida} de salida` +
              (prueba.tokens_razonamiento ? `, de los cuales ${prueba.tokens_razonamiento} razonando` : "") +
              (prueba.reintentado_sin_razonar ? " · hizo falta reintentar sin razonamiento" : "")
            }
          />
        </div>
      )}

      {uso.data && uso.data.por_tarea.length > 0 && (
        <div className="mt-5">
          <div className="text-sm font-medium text-tinta-2 mb-2">Consumo acumulado</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-tinta-3 border-b border-grid">
                <tr>
                  <th className="text-left py-2 pr-3">Tarea</th>
                  <th className="text-left py-2 pr-3">Modelo</th>
                  <th className="text-right py-2 pr-3">Llamadas</th>
                  <th className="text-right py-2 pr-3">Entrada</th>
                  <th className="text-right py-2 pr-3">Salida</th>
                  <th className="text-right py-2 pr-3" title="Tokens de entrada servidos desde el caché del proveedor">
                    Caché
                  </th>
                  <th className="text-right py-2 pr-3">Coste</th>
                </tr>
              </thead>
              <tbody>
                {uso.data.por_tarea.map((t) => (
                  <tr key={`${t.tarea}-${t.modelo}`} className="border-b border-grid">
                    <td className="py-2 pr-3 font-mono text-marca">{t.tarea}</td>
                    <td className="py-2 pr-3 text-tinta-3">{t.modelo}</td>
                    <td className="py-2 pr-3 text-right">{t.llamadas}</td>
                    <td className="py-2 pr-3 text-right">{t.tokens_entrada.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right">{t.tokens_salida.toLocaleString()}</td>
                    <td className={`py-2 pr-3 text-right ${t.pct_cache > 0 ? "text-estado-ok" : "text-tinta-3"}`}>
                      {t.pct_cache}%
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {t.costo ? `$${t.costo.toFixed(4)}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-tinta-3">
            La columna <b>Caché</b> es el porcentaje de tokens de entrada que sirvió el
            proveedor desde su caché. Si en una tarea repetida se queda en 0 %, el bloque de
            sistema de su prompt está cambiando entre llamadas: revísalo en Prompts.
          </p>
        </div>
      )}

      {uso.data && !uso.data.configurada && (
        <p className="mt-3 text-sm text-estado-serio">
          Todavía no hay clave de API: las páginas que usan IA lo dirán al intentar usarla.
        </p>
      )}
    </Card>
  );
}

export default function ConfiguracionPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: SettingsAPI.all,
  });
  const [borrador, setBorrador] = useState<Record<string, Valores>>({});

  useEffect(() => {
    if (data) setBorrador(JSON.parse(JSON.stringify(data.valores)));
  }, [data]);

  const grupos = useMemo(() => (data ? Object.keys(data.schema) : []), [data]);

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      <PageHeader
        title="Configuración"
        subtitle="Todo el comportamiento del sistema se ajusta desde aquí, sin tocar el código."
      />

      <HelpBox title="¿Cómo funciona esta pantalla?">
        <p>
          Cada opción se guarda en la base de datos y surte efecto en la siguiente petición:
          no hace falta reiniciar ni volver a desplegar.
        </p>
        <p>
          Los campos marcados con una variable de entorno también se pueden fijar en el
          <code className="font-mono"> .env</code>. Si escribes un valor aquí, <b>manda el panel</b>;
          si lo dejas vacío, vuelve a mandar el <code className="font-mono">.env</code>. La propia
          etiqueta te dice cuál está vigente ahora mismo.
        </p>
        <p>
          Las contraseñas y claves nunca se devuelven: verás el campo marcado como guardado.
          Déjalo en blanco para conservarlo y escribe encima solo si quieres reemplazarlo.
        </p>
        <p>
          <b>Se guarda por bloque.</b> Cada tarjeta tiene su botón; lo que escribas en las
          demás no se pierde al guardar una.
        </p>
      </HelpBox>

      {isLoading && (
        <div className="flex items-center gap-2 text-tinta-3">
          <Spinner /> Cargando configuración…
        </div>
      )}
      {error && <ErrorBanner message={(error as Error).message} />}

      {data && (
        <>
          <TarjetaIA />
          <TarjetaAuditor />
          {grupos.map((g) => (
            <TarjetaGrupo
              key={g}
              grupo={g}
              datos={data}
              borrador={borrador}
              setBorrador={setBorrador}
            />
          ))}
        </>
      )}
    </div>
  );
}

// Lectura de transcripciones del Auditor de Encuestas.
//
// «Probar» habla de verdad con él y dice qué encuestas ve: dar por buena una
// configuración porque el campo no está vacío ya costó meses de una vía que
// nunca funcionó. El botón de vaciar el caché está al lado del automático
// (el caché vence solo) porque quien acaba de transcribir audio nuevo allí no
// tiene por qué esperar a que venza.
function TarjetaAuditor() {
  const [prueba, setPrueba] = useState<PruebaAuditor | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const probar = useMutation({
    mutationFn: SettingsAPI.probarAuditor,
    onSuccess: (r) => {
      setPrueba(r);
      setAviso(null);
    },
    onError: () => setPrueba(null),
  });

  const vaciar = useMutation({
    mutationFn: SettingsAPI.invalidarCacheAuditor,
    onSuccess: (r) => setAviso(r.mensaje),
  });

  return (
    <Card className="mb-5">
      <h2 className="text-lg font-semibold text-tinta mb-1">
        Transcripciones del Auditor de Encuestas
      </h2>
      <p className="text-sm text-tinta-3 mb-3">
        Con esto conectado, la <strong>transcripción del audio</strong> de cada entrevista
        aparece en «Preguntas abiertas» como una pregunta más: se puede analizar entera,
        por muestra o en lotes, cruzada por las variables del formulario, con el prompt que
        tú edites. Solo se lee; el audio sigue siendo del Auditor. Los campos se rellenan
        abajo, en «{"Auditor de Encuestas (transcripciones)"}».
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn" disabled={probar.isPending} onClick={() => probar.mutate()}>
          {probar.isPending ? "Probando…" : "Probar la conexión"}
        </button>
        <button
          className="btn-sec"
          disabled={vaciar.isPending}
          onClick={() => vaciar.mutate()}
          title="Vuelve a pedir las transcripciones al Auditor en la próxima lectura"
        >
          {vaciar.isPending ? "Vaciando…" : "Volver a leerlas"}
        </button>
        {(probar.isPending || vaciar.isPending) && <Spinner />}
      </div>

      {probar.isError && (
        <div className="mt-3"><ErrorBanner message={(probar.error as Error).message} /></div>
      )}
      {vaciar.isError && (
        <div className="mt-3"><ErrorBanner message={(vaciar.error as Error).message} /></div>
      )}
      {aviso && <div className="mt-3"><OkBanner message={aviso} /></div>}

      {prueba && (
        <>
          <div className="mt-3"><OkBanner message={prueba.mensaje} /></div>
          {prueba.encuestas.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-tinta-3 border-b border-grid">
                  <tr>
                    <th className="text-left py-2 pr-3">Encuesta en el Auditor</th>
                    <th className="text-left py-2 pr-3">Proyecto / formulario de ODK</th>
                    <th className="text-right py-2 pr-3">Con transcripción</th>
                    <th className="text-right py-2 pr-3">Caracteres</th>
                  </tr>
                </thead>
                <tbody>
                  {prueba.encuestas.map((e) => (
                    <tr key={e.encuesta_id} className="border-b border-grid">
                      <td className="py-2 pr-3">{e.nombre}</td>
                      <td className="py-2 pr-3 font-mono text-marca">
                        {e.odk_project_id} / {e.odk_form_id}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {e.con_transcripcion.toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-right">{e.caracteres.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-tinta-3 mt-2">
                Las dos partes se cruzan por el <code>{prueba.llave_de_cruce}</code> de ODK, así
                que el formulario tiene que ser el mismo de los dos lados. Si aquí sale un
                formulario que no está en tu lista de encuestas, es que el Auditor audita uno
                que tú no tienes descargado.
              </p>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
